import { Box, CircularProgress } from "@mui/material";
import * as React from "react";

import { useDomainServices } from "@/providers/DomainServices";
import { useMeasure, type OnDimensionsChange } from "@/shared/hooks";
import { useDebouncedCallback } from "@/shared/hooks/useDebouncedCallback";
import { ErrorMonitoringService } from "@/shared/services";
import { LayoutItem, MapPanelState } from "@/shared/state/visualization";

import { type MapPanelSettingsSidebarProps } from "../../PanelSettings/MapPanelSettings";
import { SidebarViewType, useWorkspaceSidebar } from "../../sidebarContext";
import { useWorkspaceTimer } from "../../WorkspaceCtx";
import {
  useEphemeralPanelState,
  useSelectPanel,
} from "../../WorkspaceCtx/hooks";
import { DataNotFoundMessage } from "../DataNotFoundMessage";
import { NoDataMessage } from "../NoDataMessage";
import { PanelLayout } from "../PanelLayout";
import { RenderingError } from "../RenderingError";

import { ActivePointIndicator } from "./ActivePointIndicator";
import { TimestampedLocation } from "./geoTypes";
import { Legend } from "./Legend";
import styles from "./Map.module.css";
import { MapService } from "./MapService";

interface MapPanelProps {
  layout: LayoutItem;
  state: MapPanelState;
}

export function MapPanel({ layout, state }: MapPanelProps) {
  const timer = useWorkspaceTimer();
  const sidebar = useWorkspaceSidebar();
  const selectPanel = useSelectPanel();
  const [, updateEphemeralState] = useEphemeralPanelState();

  const { files: fileService } = useDomainServices();
  const mapContainer = React.useRef<HTMLDivElement | null>(null);
  const [mapService, setMapService] = React.useState<MapService | null>(null);
  const [error, setError] = React.useState<Error | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [singleActiveLocation, setSingleActiveLocation] =
    React.useState<TimestampedLocation | null>(null);

  const hasInitialViewport = state.initParams?.viewport !== undefined;
  const hasLoadedInitialViewport = React.useRef(false);

  // Callback to write viewport changes to ephemeral state
  const onViewportChange = React.useCallback(
    (viewport: {
      center: { lng: number; lat: number };
      zoom: number;
      bearing: number;
      pitch: number;
    }) => {
      updateEphemeralState(state.id, { viewport });
    },
    [updateEphemeralState, state.id],
  );

  React.useEffect(() => {
    const container = mapContainer.current;

    if (container === null || error !== null) {
      return;
    }

    const abortController = new AbortController();

    const _mapService = new MapService({
      abortController: abortController,
      mapContainer: container,
      fileService: fileService,
      setIsLoading: setIsLoading,
      timer: timer,
      setSingleActiveLocation: setSingleActiveLocation,
      onViewportChange: onViewportChange,
      initialViewport: state.initParams?.viewport,
    });

    _mapService
      .initialize()
      .then(() => {
        if (abortController.signal.aborted) {
          return;
        }
        setMapService(_mapService);
      })
      .catch((err) => {
        if (abortController.signal.aborted) {
          return;
        }
        const error =
          err instanceof Error
            ? err
            : new Error("Failed to initialize MapPanel", { cause: err });
        setError(error);
        ErrorMonitoringService.captureError(error);
      });

    return () => {
      // Abort after disposing to ensure all clean-up actions are complete
      _mapService.dispose();
      abortController.abort();
    };
  }, [
    error,
    mapContainer,
    fileService,
    timer,
    onViewportChange,
    state.initParams?.viewport,
  ]);

  /**
   * Update the MapServices's reference to state as it changes
   */
  React.useEffect(
    function updateState() {
      if (mapService === null) {
        return;
      }

      const abortController = new AbortController();
      mapService
        .setState(state.data, abortController.signal)
        .then(() => {
          const isFirstInitWithViewport =
            !hasLoadedInitialViewport.current && hasInitialViewport;

          if (state.data.length === 1 && !isFirstInitWithViewport) {
            mapService.centerOnPath(state.data[0].id);
          }

          hasLoadedInitialViewport.current = true;
        })
        .catch((err) => {
          if (abortController.signal.aborted) {
            return;
          }
          const error =
            err instanceof Error
              ? err
              : new Error("Failed to get latest message for MapPanel", {
                  cause: err,
                });
          setIsLoading(false);
          ErrorMonitoringService.captureError(error);
          setError(error);
        });

      return function abort() {
        abortController.abort();
      };
    },
    [hasInitialViewport, mapService, state.data],
  );

  /**
   * Redraw the image when the div is resized
   */
  const onRenderingSurfaceResize = React.useCallback(
    function onResize() {
      if (mapService === null) {
        return;
      }
      mapService?.resize();
    },
    [mapService],
  );

  const onRenderingSurfaceResizeDebounced =
    useDebouncedCallback<OnDimensionsChange>(onRenderingSurfaceResize, 100);

  const [measured] = useMeasure<HTMLDivElement>({
    onDimensionsChange: onRenderingSurfaceResizeDebounced,
  });

  return (
    <PanelLayout isLoading={isLoading} state={state} layout={layout}>
      <Box
        className={styles.loadingIndicator}
        sx={{
          display: isLoading ? "block" : "none",
        }}
      >
        <CircularProgress size="2rem" />
      </Box>

      {state.data.length === 1 && singleActiveLocation && (
        // Show active point indicator if there's only one path on the map
        <ActivePointIndicator activeLocation={singleActiveLocation} />
      )}

      {state.data.length > 1 && (
        // Show legend if there are multiple paths
        <Legend
          state={state}
          centerOnPath={(pathId) => mapService?.centerOnPath(pathId)}
        />
      )}

      <div
        id="map"
        ref={(node) => {
          if (node !== null) {
            measured(node);
          }
          mapContainer.current = node;
        }}
        style={{ width: "100%", height: "100%" }}
      />
      <RenderingError error={error} onClearError={() => setError(null)} />
      <NoDataMessage
        panelData={state.data}
        onAddDataAction={() => {
          // Open the sidebar and spotlight the paths field
          selectPanel(state.id);
          sidebar.openView<MapPanelSettingsSidebarProps>({
            type: SidebarViewType.PanelSettings,
            data: {
              field: "Paths",
              spotlightKey: performance.now(),
            },
          });
        }}
      />
      <DataNotFoundMessage panelId={state.id} />
    </PanelLayout>
  );
}
